// KPI kanıt paneli + raporlanan rozeti saf mantığı (M16 T18/T23). Bileşenler ince kalır;
// burada: dosya boyutu/tür etiketi, tarama rozeti, konum metni, çoklu hedef seçici varsayılanları,
// raporlanan/hesaplanan özet türetimi, hücre geçmişi satırları. vitest ile kilitli.

import {
  EVIDENCE_RETENTION_POLICY_KEEP,
  EVIDENCE_RETENTION_POLICY_RE,
  EVIDENCE_RETENTION_POLICY_SYSTEM,
  formatKpiNumber,
  type EvidenceFormLink,
  type KpiEvidenceLocator,
  type KpiResolvedCell,
} from "@mahir/shared";
import type { KpiResolvedIndicator } from "@mahir/shared";
import { KPI_EVIDENCE_COPY } from "./kpi-evidence-copy";
import { KPI_COPY } from "./kpi-ui";

// ─── Dosya ───────────────────────────────────────────────────────────────────

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes.toString()} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb).toLocaleString("tr-TR")} KB`;
  const mb = kb / 1024;
  return `${mb.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} MB`;
}

export type FileKind = "PDF" | "XLSX" | "DOCX" | "CSV" | "IMG" | "DOSYA";

export function fileKindLabel(mimeType: string | null | undefined, fileName: string): FileKind {
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  const mime = (mimeType ?? "").toLowerCase();
  if (mime === "application/pdf" || ext === "pdf") return "PDF";
  if (
    mime.includes("spreadsheet") ||
    mime === "application/vnd.ms-excel" ||
    ext === "xlsx" ||
    ext === "xls"
  )
    return "XLSX";
  if (mime.includes("wordprocessing") || ext === "docx") return "DOCX";
  if (mime === "text/csv" || ext === "csv") return "CSV";
  if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "webp"].includes(ext)) return "IMG";
  return "DOSYA";
}

export type ScanStatus = "pending" | "clean" | "infected" | "quarantined" | "error";
export type ScanTone = "pending" | "ok" | "blocked";

export function scanBadge(
  status: string,
  purgedAt?: string | null,
): { text: string; tone: ScanTone } {
  if (purgedAt) return { text: KPI_EVIDENCE_COPY.scan.purged, tone: "blocked" };
  switch (status) {
    case "clean":
      return { text: KPI_EVIDENCE_COPY.scan.clean, tone: "ok" };
    case "pending":
      return { text: KPI_EVIDENCE_COPY.scan.pending, tone: "pending" };
    case "infected":
      return { text: KPI_EVIDENCE_COPY.scan.infected, tone: "blocked" };
    case "quarantined":
      return { text: KPI_EVIDENCE_COPY.scan.quarantined, tone: "blocked" };
    case "error":
    default:
      // TODO-EVIDENCE-05: `error` = tarama tamamlanamadı (cron yeniden dener) — açılmaz.
      return { text: KPI_EVIDENCE_COPY.scan.error, tone: "blocked" };
  }
}

/**
 * TODO-EVIDENCE-01 — "form kanıtı · <soru kodu>" rozetleri. Aynı dosya birden çok soruya bağlıysa
 * her kod bir rozet; soru kodu tekrarlıysa (farklı yıl projeleri) tek rozet.
 */
export function formLinkLabels(formLinks: EvidenceFormLink[] | undefined): string[] {
  if (!formLinks || formLinks.length === 0) return [];
  const codes = [...new Set(formLinks.map((l) => l.questionCode))];
  return codes.map((c) => KPI_EVIDENCE_COPY.panel.formEvidence(c));
}

// ─── Dosya bazlı saklama (TODO-EVIDENCE-03) ───────────────────────────────────

export interface RetentionOption {
  value: string;
  label: string;
}

const RETENTION_PRESET_DAYS = [30, 90, 180, 365, 1095];

/** Seçim listesi: sistem · süresiz · hazır gün seçenekleri (+ mevcut özel `<n>d` değeri). */
export function retentionOptions(current: string | null | undefined): RetentionOption[] {
  const R = KPI_EVIDENCE_COPY.retention;
  const out: RetentionOption[] = [
    { value: EVIDENCE_RETENTION_POLICY_SYSTEM, label: R.system },
    { value: EVIDENCE_RETENTION_POLICY_KEEP, label: R.keep },
    ...RETENTION_PRESET_DAYS.map((d) => ({ value: `${d.toString()}d`, label: R.days(d) })),
  ];
  const cur = normalizeRetentionPolicy(current);
  if (!out.some((o) => o.value === cur)) {
    const days = Number(cur.slice(0, -1));
    out.push({ value: cur, label: R.days(days) });
  }
  return out;
}

/** Bilinmeyen/boş politika → `system` (backend `resolveRetentionDays` ile aynı kural). */
export function normalizeRetentionPolicy(policy: string | null | undefined): string {
  const p = (policy ?? "").trim().toLowerCase();
  return EVIDENCE_RETENTION_POLICY_RE.test(p) ? p : EVIDENCE_RETENTION_POLICY_SYSTEM;
}

export function formatRetentionPolicy(policy: string | null | undefined): string {
  const R = KPI_EVIDENCE_COPY.retention;
  const p = normalizeRetentionPolicy(policy);
  if (p === EVIDENCE_RETENTION_POLICY_KEEP) return R.keep;
  if (p === EVIDENCE_RETENTION_POLICY_SYSTEM) return R.system;
  return R.days(Number(p.slice(0, -1)));
}

/** Yalnız taraması temiz dosya açılır/indirilir (DR9 — "taranıyor/engellendi" pasif). */
export function canOpenEvidence(scanStatus: string, purgedAt?: string | null): boolean {
  return scanStatus === "clean" && !purgedAt;
}

export function formatLocator(locator: KpiEvidenceLocator | null | undefined): string {
  if (!locator) return "";
  if ("sheet" in locator) return `${locator.sheet} · ${locator.cell}`;
  const page = `s. ${locator.page.toString()}`;
  return locator.row !== undefined ? `${page} · satır ${locator.row.toString()}` : page;
}

export function relationLabel(relationType: string): string {
  return relationType === "import_source"
    ? KPI_EVIDENCE_COPY.panel.importSource
    : KPI_EVIDENCE_COPY.panel.linkedBy;
}

/** FileDropzone red kararı — backend ALLOWED_MIMES ile hizalı (services/evidence.ts). */
export const EVIDENCE_MAX_BYTES = 50 * 1024 * 1024;
export const EVIDENCE_ACCEPT =
  "application/pdf,.pdf,image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.xlsx,.xls,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,text/csv,.csv";
const ALLOWED_EXT = new Set(["pdf", "jpg", "jpeg", "png", "webp", "xlsx", "xls", "docx", "csv"]);

export function evidenceRejectReason(file: { name: string; size: number }): string | null {
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  if (!ALLOWED_EXT.has(ext)) return "Desteklenmeyen dosya tipi";
  if (file.size > EVIDENCE_MAX_BYTES) return "Dosya 50 MB sınırını aşıyor";
  return null;
}

// ─── Çoklu hedef seçici (gösterge × yıl) ─────────────────────────────────────

export interface EvidenceTargetOption {
  indicatorId: string;
  metricId: string;
  label: string;
  categoryLabel: string | null;
  /** açık hücrenin göstergesiyle aynı üst düğüm (kategori) */
  sameCategory: boolean;
  isCurrent: boolean;
}

export function targetKey(metricId: string, year: number): string {
  return `${metricId}|${year.toString()}`;
}

export function parseTargetKey(key: string): { metricId: string; year: number } | null {
  const i = key.lastIndexOf("|");
  if (i < 0) return null;
  const year = Number(key.slice(i + 1));
  if (!Number.isInteger(year)) return null;
  return { metricId: key.slice(0, i), year };
}

/**
 * Seçilebilir göstergeler: yaprak (çocuğu olmayan) ve türetilmemiş satırlar. Açık hücrenin
 * göstergesi `isCurrent`, aynı parentId altındakiler `sameCategory` (DR9 — aynı kategori önerisi).
 * Sıra: önce açık gösterge, sonra aynı kategori, sonra kalanlar (grid sırası korunur).
 */
export function buildEvidenceTargetOptions(
  indicators: Pick<KpiResolvedIndicator, "id" | "metricId" | "label" | "parentId" | "isDerived">[],
  currentMetricId: string,
): EvidenceTargetOption[] {
  const hasChild = new Set(indicators.map((i) => i.parentId).filter((p): p is string => !!p));
  const byId = new Map(indicators.map((i) => [i.id, i]));
  const current = indicators.find((i) => i.metricId === currentMetricId);
  const currentParent = current?.parentId ?? null;
  const out: EvidenceTargetOption[] = [];
  for (const ind of indicators) {
    if (hasChild.has(ind.id) || ind.isDerived) continue;
    const parent = ind.parentId ? byId.get(ind.parentId) : undefined;
    out.push({
      indicatorId: ind.id,
      metricId: ind.metricId,
      label: ind.label,
      categoryLabel: parent?.label ?? null,
      sameCategory: currentParent !== null && ind.parentId === currentParent,
      isCurrent: ind.metricId === currentMetricId,
    });
  }
  const rank = (o: EvidenceTargetOption): number => (o.isCurrent ? 0 : o.sameCategory ? 1 : 2);
  return out
    .map((o, i) => ({ o, i }))
    .sort((a, b) => rank(a.o) - rank(b.o) || a.i - b.i)
    .map((x) => x.o);
}

/** Varsayılan seçim: yalnız açık hücre (DR9). */
export function defaultTargetKeys(currentMetricId: string, currentYear: number): Set<string> {
  return new Set([targetKey(currentMetricId, currentYear)]);
}

/** "Aynı kategoridekileri seç": aynı kategori + açık gösterge × verilen yıl. */
export function sameCategoryKeys(options: EvidenceTargetOption[], year: number): Set<string> {
  const keys = new Set<string>();
  for (const o of options) if (o.isCurrent || o.sameCategory) keys.add(targetKey(o.metricId, year));
  return keys;
}

export function targetsFromKeys(
  keys: Iterable<string>,
  entityId: string,
): { metricId: string; entityId: string; year: number }[] {
  const out: { metricId: string; entityId: string; year: number }[] = [];
  for (const k of keys) {
    const p = parseTargetKey(k);
    if (p) out.push({ metricId: p.metricId, entityId, year: p.year });
  }
  return out;
}

// ─── Raporlanan değer özeti (T23) ────────────────────────────────────────────

export interface ReportedSummary {
  /** hesaplanan değer (formül çalıştıysa); sabit ya da hesap yoksa null */
  computed: number | null;
  reported: number | null;
  /** hesaplanan − raporlanan (resolve-core `reportedMismatch`); yoksa null */
  mismatch: number | null;
  pinned: boolean;
  /** hücrede görünen değer raporlanan mı (sabit ya da hesap yapılamadı) */
  showsReported: boolean;
}

/**
 * resolve-core sözleşmesi: formül çalışıp raporlanandan farklıysa `value` = hesap ve
 * `reportedMismatch = hesap − raporlanan`; sabit ya da hesap yapılamadıysa `value` = raporlanan ve
 * mismatch yok. Bu yüzden raporlanan = mismatch varsa `value − mismatch`, yoksa `value`.
 */
export function reportedSummary(cell: KpiResolvedCell | undefined): ReportedSummary | null {
  if (!cell?.isReported) return null;
  const value = typeof cell.value === "number" ? cell.value : null;
  const pinned = cell.pinned === true;
  if (typeof cell.reportedMismatch === "number" && value !== null) {
    return {
      computed: value,
      reported: value - cell.reportedMismatch,
      mismatch: cell.reportedMismatch,
      pinned,
      showsReported: false,
    };
  }
  return { computed: null, reported: value, mismatch: null, pinned, showsReported: true };
}

export function formatReportedNumber(v: number | null, percent: boolean): string {
  if (v === null) return "—";
  return formatKpiNumber(v, percent ? { percent: true } : {});
}

// ─── Hücre geçmişi (Geçmiş sekmesi — ayrı endpoint yok, hücre alanlarından) ──

export interface HistoryRow {
  label: string;
  value: string;
}

export function formatHistoryDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function historyRows(
  cell: KpiResolvedCell | undefined,
  currentUserId: string | null,
): HistoryRow[] {
  const H = KPI_EVIDENCE_COPY.history;
  if (!cell?.valueId) return [];
  const rows: HistoryRow[] = [];
  if (cell.updatedAt) rows.push({ label: H.lastUpdate, value: formatHistoryDate(cell.updatedAt) });
  const who =
    cell.updatedBy && currentUserId && cell.updatedBy === currentUserId
      ? H.you
      : cell.updatedByKind === "client"
        ? H.client
        : cell.updatedByKind === "internal"
          ? H.internal
          : null;
  if (who) rows.push({ label: H.by, value: who });
  if (typeof cell.version === "number")
    rows.push({ label: H.version, value: cell.version.toString() });
  if (cell.source) {
    const kind = H.sourceKinds[cell.source.kind];
    rows.push({ label: H.source, value: kind });
  }
  if (cell.workflowState) {
    const label = KPI_COPY.workflow[cell.workflowState as keyof typeof KPI_COPY.workflow];
    rows.push({ label: H.workflow, value: label ?? cell.workflowState });
  }
  if (cell.note) rows.push({ label: H.note, value: cell.note });
  if (cell.clarificationNote)
    rows.push({ label: H.clarificationNote, value: cell.clarificationNote });
  return rows;
}
